import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { ymd } from '@/components/ui/PeriodPicker';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { listTdsSections } from '@/api/accountsBooks';
import { alterChallan, challanDue, createChallan, type ChallanDetails, type ChallanInput } from '@/api/accountsTds';
import { ChipTag, DateField, F, FormFooter, FormTitle, Msgs, Posting, Sec, type PostLine } from './voucherFormParts';
import { dd, inr, r2 } from './voucherFormat';
import { Grid } from './tradeParts';
import type { FormProps } from './useVoucherForm';

/*
 * TDS challan — the Payment voucher's second mode: the section and the month the TDS was deducted
 * in fill the TDS due from the books (and the interest if it is late); the challan's BSR code,
 * serial and date are what the bank's counterfoil shows. Posts Dr TDS Payable · Dr Interest on TDS ·
 * Dr Late Fee – TDS · Cr bank, and covers that month's deductions of the section.
 */

const MODES = ['Net Banking', 'NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque'];
const prevMonth = (d: string) => { const [y, m] = d.split('-').map(Number); return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`; };

export function TdsChallanForm({ voucher, ledgers, nextNo, onClose, onGeneral }: FormProps & { onGeneral?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const today = ymd(new Date());
  const banks = ledgers.filter((l) => l.kind === 'bank');
  const d = voucher?.details as unknown as ChallanDetails | undefined;
  const [s, setS] = useState(() => ({
    date: voucher?.date ?? today, section: d?.section ?? '', month: d?.month ?? prevMonth(today),
    tax: d?.tax ?? 0, interest: d?.interest ?? 0, fee: d?.fee ?? 0, taxEdited: !!voucher, intEdited: !!voucher,
    bank: (voucher?.lines.find((l) => banks.some((b) => b.id === l.ledgerId))?.ledgerId ?? banks[0]?.id ?? '') as number | '',
    bsr: d?.bsr ?? '', serial: d?.serial ?? '', mode: voucher?.mode ?? 'Net Banking', narr: voucher?.narration ?? '',
  }));
  const set = (p: Partial<typeof s>) => setS((x) => ({ ...x, ...p }));
  const secQ = useQuery({ queryKey: ['acc-books', 'tds-sections'], queryFn: listTdsSections });
  const dueQ = useQuery({
    queryKey: ['acc-books', 'tds-challan-due', s.section, s.month, s.date, voucher?.id],
    queryFn: () => challanDue({ section: s.section, month: s.month, date: s.date, excludeVoucher: voucher?.id }),
    enabled: !!s.section && /^\d{4}-\d{2}$/.test(s.month),
  });
  const due = dueQ.data;
  // The TDS to deposit and the interest come from the books, until typed over.
  useEffect(() => {
    if (!due) return;
    setS((x) => ({ ...x, ...(x.taxEdited ? {} : { tax: due.toDeposit }), ...(x.intEdited ? {} : { interest: due.interest }) }));
  }, [due]);
  const sec = secQ.data?.find((x) => x.code === s.section);
  const bank = banks.find((b) => b.id === s.bank);
  const total = r2(s.tax + s.interest + s.fee);

  const block: string[] = [];
  if (!s.section) block.push('Choose the TDS section.');
  if (!s.month) block.push('Choose the month the TDS was deducted in.');
  if (s.month > s.date.slice(0, 7)) block.push('The challan can’t be for a month after its date.');
  if (!(s.tax > 0)) block.push('Enter the TDS amount.');
  if (!bank) block.push('Choose the bank account the challan was paid from.');
  if (!/^\d{7}$/.test(s.bsr.trim())) block.push('Enter the bank’s BSR code (7 digits).');
  if (!/^\d{5}$/.test(s.serial.trim())) block.push('Enter the challan serial no. (5 digits).');
  const warn: string[] = [];
  if (due && s.tax > due.toDeposit + 0.004) warn.push(`${inr(s.tax - due.toDeposit)} more than the TDS still to deposit for ${s.section} ${s.month} — the excess stays unadjusted on the challan.`);
  if (due && due.toDeposit > 0 && s.tax + 0.004 < due.toDeposit) warn.push(`${inr(due.toDeposit - s.tax)} of ${s.month}'s TDS stays undeposited after this challan.`);
  if (due && s.date > due.due && s.interest + 0.004 < due.interest) warn.push(`Paid after the due date (${dd(due.due)}): interest at 1.5% a month comes to ${inr(due.interest)}.`);

  const lines: PostLine[] = [
    { l: sec ? `TDS Payable – ${sec.code === 'RENT' ? 'Rent' : sec.code}` : 'TDS Payable', a: s.tax, sub: [`Deducted in ${s.month}`] },
    { l: 'Interest on TDS', a: s.interest }, { l: 'Late Fee – TDS', a: s.fee },
    { l: bank?.name ?? 'Bank', a: -total, sub: [`Challan ${s.serial || '—'} · BSR ${s.bsr || '—'} · ${s.mode}`] },
  ];
  const build = (): ChallanInput => ({
    date: s.date, section: s.section, month: s.month, tax: s.tax, interest: s.interest, fee: s.fee, bankLedgerId: s.bank as number,
    bsr: s.bsr.trim(), serial: s.serial.trim(), mode: s.mode, narration: s.narr.trim() || undefined,
  });
  const save = useMutation({
    mutationFn: (b: ChallanInput) => (voucher ? alterChallan(voucher.id, b) : createChallan(b)),
    onSuccess: (v) => { toast.push(`TDS challan ${v.voucherNo} ${voucher ? 'altered' : 'saved'}`, 'success'); qc.invalidateQueries({ queryKey: ['acc-books'] }); onClose(); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const money = (val: number, on: (v: number) => void) => (
    <Input type="number" step="0.01" min={0} className="text-right font-mono" value={val || ''} placeholder="0.00" onChange={(e) => on(r2(Number(e.target.value) || 0))} />
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <FormTitle title="Payment" no={voucher?.voucherNo ?? nextNo} chip={<ChipTag tone="r">TDS challan</ChipTag>}
          extra={
            <span className="ml-auto inline-flex overflow-hidden rounded-md border border-[var(--border-color)] text-[11px] font-bold">
              <button type="button" disabled={!onGeneral} onClick={onGeneral} className="px-2.5 py-1 text-[var(--text-muted)] hover:text-accent disabled:cursor-not-allowed">Payment</button>
              <span className="bg-accent/15 px-2.5 py-1 text-accent">TDS challan</span>
            </span>
          } />
      }
      footer={<FormFooter left={<>Challan total <b>{inr(total)}</b></>} version={voucher ? voucher.version + 1 : undefined} saveLabel="Save Challan" blocked={block.length > 0} saving={save.isPending} onClose={onClose} onSave={() => save.mutate(build())} />}
    >
      <Grid>
        <DateField value={s.date} max={today} onChange={(v) => set({ date: v })} />
        <F label="TDS section *">
          <Select value={s.section} onChange={(e) => set({ section: e.target.value, taxEdited: false, intEdited: false })}>
            <option value="">Choose…</option>
            {(secQ.data ?? []).map((x) => <option key={x.code} value={x.code}>{x.code} · {x.nature}</option>)}
          </Select>
        </F>
        <F label="For month (deducted in) *">
          <Input type="month" max={s.date.slice(0, 7)} value={s.month} onChange={(e) => set({ month: e.target.value, taxEdited: false, intEdited: false })} />
        </F>
        <F label="Paid from *">
          <Select value={s.bank} onChange={(e) => set({ bank: e.target.value === '' ? '' : Number(e.target.value) })}>
            <option value="">Choose…</option>
            {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </F>
      </Grid>
      {due && (
        <div className="mt-2 rounded-md border border-info/30 bg-info/10 px-2.5 py-1.5 text-xs text-[var(--text-secondary)]">
          {s.section} · {s.month}: <b>{inr(due.deducted)}</b> deducted on {due.deductions} bill{due.deductions === 1 ? '' : 's'}, <b>{inr(due.deposited)}</b> already deposited — <b>{inr(due.toDeposit)}</b> to deposit by <b>{dd(due.due)}</b>.
        </div>
      )}

      <Sec>Challan (as on the counterfoil)</Sec>
      <Grid>
        <F label="BSR code *"><Input inputMode="numeric" maxLength={7} className="font-mono" value={s.bsr} placeholder="0510308" onChange={(e) => set({ bsr: e.target.value.replace(/\D/g, '') })} /></F>
        <F label="Challan serial no. *"><Input inputMode="numeric" maxLength={5} className="font-mono" value={s.serial} placeholder="00001" onChange={(e) => set({ serial: e.target.value.replace(/\D/g, '') })} /></F>
        <F label="Mode"><Select value={s.mode} onChange={(e) => set({ mode: e.target.value })}>{MODES.map((m) => <option key={m}>{m}</option>)}</Select></F>
      </Grid>

      <Sec>Amount</Sec>
      <Grid>
        <F label={<>TDS {s.taxEdited && due ? <button type="button" className="ml-1 text-[11px] font-bold text-accent" onClick={() => set({ tax: due.toDeposit, taxEdited: false })}>↺ {inr(due.toDeposit)}</button> : null}</>}>{money(s.tax, (v) => set({ tax: v, taxEdited: true }))}</F>
        <F label={<>Interest (201(1A)) {s.intEdited && due ? <button type="button" className="ml-1 text-[11px] font-bold text-accent" onClick={() => set({ interest: due.interest, intEdited: false })}>↺ {inr(due.interest)}</button> : null}</>}>{money(s.interest, (v) => set({ interest: v, intEdited: true }))}</F>
        <F label="Late fee (234E)">{money(s.fee, (v) => set({ fee: v }))}</F>
      </Grid>

      <Msgs block={block} warn={warn} />
      <F label="Narration" className="mt-3">
        <Textarea rows={2} maxLength={1000} value={s.narr} placeholder={`TDS challan ${s.section || '…'} – ${s.month}`} onChange={(e) => set({ narr: e.target.value })} />
      </F>
      <Sec>Posting</Sec>
      <Posting lines={lines} />
    </Modal>
  );
}
